<script setup lang="ts">
import {
  Bot,
  CalendarClock,
  FileText,
  RotateCcw,
  Paperclip,
  Pause,
  Search,
  Send,
  Sparkles,
  UsersRound,
} from 'lucide-vue-next'
import type {
  ManagerExecutionProjection,
  ManagerMessageProjection,
  TaskOutputProjection,
  TaskProjection,
  WorkerExecutionProjection,
  ManagerPlanProjection,
  SendManagerMessageResult,
} from '@skiphr/contracts'
import { collection, doc, limit, onSnapshot, orderBy, query } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import PlanDiagram from '@/components/PlanDiagram.vue'
import { firebaseClient } from '@/firebase'
import { useAuthStore } from '@/stores/auth'
import { useAgentStore, useUsageStore } from '@/stores/domain'

const draft = ref('')
const queued = ref(false)
const sending = ref(false)
const sendError = ref('')
const messages = ref<readonly ManagerMessageProjection[]>([])
const plan = ref<ManagerPlanProjection | undefined>()
const confirming = ref(false)
const auth = useAuthStore()
const executions = ref<readonly ManagerExecutionProjection[]>([])
const chatScroll = ref<HTMLElement>()
const tasks = ref<readonly TaskProjection[]>([])
const workerRuns = ref<readonly WorkerExecutionProjection[]>([])
const outputs = ref<readonly TaskOutputProjection[]>([])
const { agents } = storeToRefs(useAgentStore())
const teamUnsubscribers: (() => void)[] = []
let initialScrollDone = false
const now = ref(Date.now())
let unsubscribe: (() => void) | undefined
let unsubscribePlan: (() => void) | undefined
let unsubscribeExecutions: (() => void) | undefined
let ticker: ReturnType<typeof setInterval> | undefined

const planAwaitingConfirmation = computed(() => plan.value?.status === 'awaiting_confirmation')
const { balance } = storeToRefs(useUsageStore())
const setupCredits = computed(() => plan.value?.estimate?.setupCredits ?? 0)
const creditsShort = computed(() => planAwaitingConfirmation.value && (balance.value <= 0 || balance.value < setupCredits.value))
const credits = (value: number) => Math.round(value).toLocaleString('en-US')

const planStatusLabel = computed(() => {
  if (!plan.value) return ''
  if (plan.value.status === 'awaiting_confirmation') return 'Awaiting your confirmation'
  if (plan.value.status === 'confirmed') return 'Confirmed — executing'
  if (plan.value.status === 'executed') return 'Executed'
  return 'Draft'
})

const ACTIVE_STATUSES: ReadonlySet<string> = new Set(['queued', 'starting', 'streaming', 'waiting'])
const ACTIVITY_LABELS: Record<string, string> = {
  save_plan: 'Drafting your plan', get_plan: 'Reviewing the plan', create_project: 'Setting up a project', create_goal: 'Setting a goal',
  create_agent: 'Adding an agent to your team', list_agent_templates: 'Choosing the right agents', assign_task: 'Assigning work',
  create_workflow: 'Building a workflow', start_workflow: 'Starting a workflow', create_schedule: 'Scheduling recurring work',
  create_operational_contract: 'Defining how work is handed off', request_connection: 'Requesting access to a tool',
  create_browser_profile: 'Preparing a browser profile', search_knowledge: 'Searching your knowledge', generate_image: 'Creating an image',
  generate_video: 'Creating a video', send_owner_message: 'Sending you a message', request_phone_number: 'Requesting a phone number',
  list_inbox_messages: 'Checking the inbox', read_inbox_message: 'Reading an email', list_sms_messages: 'Checking texts',
  get_inbox_address: 'Checking the inbox', get_advisor_brief: 'Reviewing performance',
}
/** The Manager turn in progress, if any. */
const STALE_AFTER_MS = 25 * 60_000
const activeExecution = computed(() => executions.value.find((execution) =>
  ACTIVE_STATUSES.has(execution.status) && now.value - execution.updatedAt < STALE_AFTER_MS))
const managerIsStreaming = computed(() =>
  Boolean(activeExecution.value) || messages.value.some((message) => message.status === 'streaming'),
)
/** Shown until the Manager's reply starts streaming in. */
const showWorking = computed(() => {
  if (sending.value) return true
  const execution = activeExecution.value
  if (!execution) return false
  return !messages.value.some((message) => message.role === 'manager' && message.executionId === execution.id && message.body.trim())
})
const workingStartedAt = computed(() => {
  const execution = activeExecution.value
  if (!execution) return now.value
  return messages.value.find((message) => message.role === 'user' && message.executionId === execution.id)?.createdAt
    ?? execution.createdAt ?? execution.startedAt ?? execution.updatedAt
})
const elapsed = computed(() => {
  const seconds = Math.max(0, Math.floor((now.value - workingStartedAt.value) / 1_000))
  return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, '0')}s`
})
const workingLabel = computed(() => {
  const execution = activeExecution.value
  if (!execution) return 'Sending your message'
  if (execution.status === 'queued') return 'Queued, starting shortly'
  if (execution.status === 'starting') return 'Getting up to speed on your workspace'
  if (execution.activity && now.value - (execution.activityAt ?? 0) < 120_000) return ACTIVITY_LABELS[execution.activity] ?? 'Working on it'
  return 'Thinking it through'
})
const workingSlow = computed(() => now.value - workingStartedAt.value > 45_000)

/** Opens at the latest message, then follows new content unless the user has scrolled up to read. */
let followLatest = true
const onChatScroll = () => {
  const element = chatScroll.value
  if (element) followLatest = element.scrollHeight - element.scrollTop - element.clientHeight < 160
}
const scrollToLatest = async (force: boolean) => {
  const element = chatScroll.value
  if (!element || (!force && !followLatest)) return
  await nextTick()
  element.scrollTo({ top: element.scrollHeight, behavior: initialScrollDone ? 'smooth' : 'auto' })
  initialScrollDone = true
  followLatest = true
}
watch(
  () => [messages.value.length, messages.value.at(-1)?.body.length, showWorking.value, plan.value?.version, teamActivity.value.length],
  () => {
    if (!messages.value.length) return
    void scrollToLatest(!initialScrollDone || messages.value.at(-1)?.role === 'user')
  },
  { flush: 'post' },
)

type TeamState = 'queued' | 'working' | 'done' | 'failed'
const TEAM_STATE_LABELS: Record<TeamState, string> = { queued: 'Queued', working: 'Working', done: 'Done', failed: 'Failed' }
const RECENT_TASK_MS = 3 * 24 * 60 * 60_000
/** Recent agent tasks with their live status and delivered files. */
const teamActivity = computed(() => tasks.value
  .filter((task) => !task.supersededAt && now.value - task.updatedAt < RECENT_TASK_MS)
  .slice(0, 6)
  .map((task) => {
    const run = workerRuns.value.find((item) => item.taskId === task.id)
    const live = run?.status ?? ''
    const state: TeamState = ['starting', 'streaming', 'waiting'].includes(live) || task.status === 'running' ? 'working'
      : task.status === 'failed' || live === 'failed' ? 'failed'
        : ['review', 'completed', 'done'].includes(task.status) || live === 'completed' ? 'done' : 'queued'
    return {
      id: task.id, title: task.title, state, updatedAt: task.updatedAt,
      agent: agents.value.find((agent) => agent.id === task.assignedAgentId)?.name ?? 'Agent',
      files: outputs.value.filter((output) => output.taskId === task.id),
    }
  }))
const retrying = ref('')
async function retryTask(taskId: string): Promise<void> {
  if (retrying.value) return
  retrying.value = taskId
  sendError.value = ''
  try { await httpsCallable(firebaseClient.functions, 'retryTask')({ taskId }) }
  catch (error) { sendError.value = error instanceof Error ? error.message.replace('Firebase: ', '') : 'Retry failed.' }
  finally { retrying.value = '' }
}
const teamWorking = computed(() => teamActivity.value.filter((item) => item.state === 'working' || item.state === 'queued').length)
const ago = (timestamp: number): string => {
  const minutes = Math.max(0, Math.round((now.value - timestamp) / 60_000))
  return minutes < 1 ? 'just now' : minutes < 60 ? `${minutes}m ago` : minutes < 1_440 ? `${Math.round(minutes / 60)}h ago` : `${Math.round(minutes / 1_440)}d ago`
}

const messageTime = (timestamp: number): string =>
  new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(timestamp)

onMounted(() => {
  const uid = auth.user?.uid
  if (!uid) return
  unsubscribe = onSnapshot(
    query(collection(firebaseClient.firestore, 'managerMessages', uid, 'items'), orderBy('createdAt')),
    (snapshot) => {
      messages.value = snapshot.docs.map((item) => item.data() as ManagerMessageProjection)
    },
  )
})

onMounted(() => {
  const uid = auth.user?.uid
  if (!uid) return
  ticker = setInterval(() => { now.value = Date.now() }, 1_000)
  const recent = <T>(path: string, target: { value: readonly T[] }, count: number) => onSnapshot(
    query(collection(firebaseClient.firestore, path, uid, 'items'), orderBy(path === 'taskOutputs' ? 'createdAt' : 'updatedAt', 'desc'), limit(count)),
    (snapshot) => { target.value = snapshot.docs.map((item) => item.data() as T) })
  teamUnsubscribers.push(recent('tasks', tasks, 12), recent('workerExecutions', workerRuns, 20), recent('taskOutputs', outputs, 40))
  unsubscribeExecutions = onSnapshot(
    query(collection(firebaseClient.firestore, 'managerExecutions', uid, 'items'), orderBy('updatedAt', 'desc'), limit(5)),
    (snapshot) => { executions.value = snapshot.docs.map((item) => item.data() as ManagerExecutionProjection) },
  )
  unsubscribePlan = onSnapshot(doc(firebaseClient.firestore, 'managerPlans', uid), (snapshot) => {
    plan.value = snapshot.exists() ? (snapshot.data() as ManagerPlanProjection) : undefined
  })
})

onBeforeUnmount(() => {
  unsubscribe?.()
  unsubscribePlan?.()
  unsubscribeExecutions?.()
  teamUnsubscribers.forEach((stop) => stop())
  if (ticker) clearInterval(ticker)
})

async function confirmPlan(): Promise<void> {
  if (confirming.value) return
  confirming.value = true
  sendError.value = ''
  try {
    const confirm = httpsCallable<Record<string, never>, SendManagerMessageResult>(
      firebaseClient.functions,
      'confirmManagerPlan',
    )
    await confirm({})
  } catch (error) {
    sendError.value =
      error instanceof Error ? error.message.replace('Firebase: ', '') : 'Confirmation failed.'
  } finally {
    confirming.value = false
  }
}

function requestChanges(): void {
  draft.value = 'I would like to change the plan: '
}

async function sendMessage(): Promise<void> {
  const body = draft.value.trim()
  if (!body || sending.value) return
  sending.value = true
  sendError.value = ''
  try {
    const send = httpsCallable<
      { message: string; queueWithoutInterrupting: boolean; attachmentIds: string[] },
      SendManagerMessageResult
    >(firebaseClient.functions, 'sendManagerMessage')
    await send({ message: body, queueWithoutInterrupting: queued.value, attachmentIds: [] })
    draft.value = ''
  } catch (error) {
    sendError.value =
      error instanceof Error ? error.message.replace('Firebase: ', '') : 'Message failed.'
  } finally {
    sending.value = false
  }
}

</script>

<template>
  <div class="manager-view">
    <header class="manager-header">
      <div>
        <div class="manager-title-row">
          <span class="manager-avatar"><Sparkles :size="19" /></span>
          <div>
            <h1>Your Manager</h1>
            <span class="presence"><i></i> {{ managerIsStreaming ? 'Working' : 'Ready' }}</span>
          </div>
        </div>
      </div>
      <div class="manager-header__actions">
        <button class="soft-button" type="button"><Search :size="16" /> <span>Search</span></button>
        <button class="soft-button" type="button">
          <Pause :size="15" /> <span>Pause work</span>
        </button>
      </div>
    </header>

    <div ref="chatScroll" class="chat-scroll" @scroll.passive="onChatScroll">
      <div v-if="sendError" class="phase-banner">
        <span><Sparkles :size="15" /></span>
        <div>
          <strong>Message not sent</strong>
          <p>{{ sendError }}</p>
        </div>
      </div>

      <div class="conversation">
        <template v-for="message in messages" :key="message.id">
          <div v-if="message.role === 'user'" class="message-row message-row--user">
            <div class="user-message">
              <p>{{ message.body }}</p>
              <time>{{ messageTime(message.createdAt) }}</time>
            </div>
            <span class="chat-avatar chat-avatar--user">A</span>
          </div>
          <div v-else class="message-row message-row--manager">
            <span class="chat-avatar chat-avatar--manager"><Sparkles :size="16" /></span>
            <div class="manager-message">
              <p>{{ message.body }}</p>
              <time>{{ messageTime(message.updatedAt) }}</time>
            </div>
          </div>
        </template>

        <div v-if="showWorking" class="message-row message-row--manager" role="status" aria-live="polite">
          <span class="chat-avatar chat-avatar--manager chat-avatar--working"><Sparkles :size="16" /></span>
          <div class="manager-working">
            <div class="manager-working__line">
              <span class="typing-dots" aria-hidden="true"><i></i><i></i><i></i></span>
              <strong>{{ workingLabel }}</strong>
              <small>{{ elapsed }}</small>
            </div>
            <p v-if="workingSlow">Bigger requests can take a few minutes. You can leave this page; the reply will be waiting here.</p>
          </div>
        </div>

        <section v-if="plan" class="plan-card" :class="{ 'plan-card--ready': planAwaitingConfirmation }">
          <header>
            <h2>{{ plan.title }}</h2>
            <span class="plan-status">{{ planStatusLabel }} · v{{ plan.version }}</span>
          </header>
          <p class="plan-outcome">{{ plan.outcome }}</p>

          <PlanDiagram :steps="plan.steps" />
          <details class="plan-details">
            <summary>Step details</summary>
            <ol class="plan-steps">
              <li v-for="(step, index) in plan.steps" :key="index">
                <span class="plan-step__kind">{{ step.creates }}</span>
                <div>
                  <strong>{{ step.title }}</strong>
                  <p>{{ step.detail }}</p>
                </div>
              </li>
            </ol>
          </details>

          <div v-if="plan.team?.length" class="plan-team">
            <div class="plan-section-head"><strong>Team</strong><span>Tasks run one at a time; each agent works only in its role.</span></div>
            <div class="plan-team__grid">
              <article v-for="member in plan.team" :key="member.name" class="plan-team__member">
                <span class="plan-team__avatar"><Bot :size="14" /></span>
                <div><strong>{{ member.name }}</strong><small>{{ member.role }}</small><p>{{ member.responsibilities }}</p></div>
              </article>
            </div>
          </div>

          <div v-if="plan.schedules?.length" class="plan-schedule">
            <div class="plan-section-head"><strong>Schedule</strong><span>{{ plan.schedules.reduce((sum, item) => sum + Math.ceil(item.runsPerMonth * item.tasksPerRun), 0) }} agent tasks a month</span></div>
            <ul>
              <li v-for="item in plan.schedules" :key="item.title">
                <span class="plan-schedule__cadence"><CalendarClock :size="13" /> {{ item.cadence }}</span>
                <div><strong>{{ item.title }}</strong><small>{{ item.tasksPerRun }} task{{ item.tasksPerRun === 1 ? '' : 's' }} per run · {{ item.agents.join(', ') }}</small></div>
              </li>
            </ul>
          </div>

          <div v-if="plan.successCriteria.length" class="plan-list">
            <strong>Success criteria</strong>
            <ul>
              <li v-for="(item, index) in plan.successCriteria" :key="index">{{ item }}</li>
            </ul>
          </div>

          <div v-if="plan.assumptions.length" class="plan-list">
            <strong>Assumptions</strong>
            <ul>
              <li v-for="(item, index) in plan.assumptions" :key="index">{{ item }}</li>
            </ul>
          </div>

          <div v-if="plan.openQuestions.length" class="plan-list plan-list--questions">
            <strong>Open questions</strong>
            <ul>
              <li v-for="(item, index) in plan.openQuestions" :key="index">{{ item }}</li>
            </ul>
          </div>

          <div v-if="plan.estimate && plan.status !== 'executed'" class="plan-estimate" :class="{ 'plan-estimate--short': creditsShort }">
            <span>Estimated <strong>{{ credits(plan.estimate.setupCredits) }} credits</strong> to set up and run once</span>
            <span v-if="plan.estimate.monthlyCredits">then about <strong>{{ credits(plan.estimate.monthlyCredits) }} credits</strong> a month</span>
            <span>Balance: <strong>{{ credits(balance) }}</strong></span>
            <RouterLink v-if="creditsShort" to="/app/billing">Buy credits to confirm</RouterLink>
          </div>

          <footer v-if="planAwaitingConfirmation" class="plan-actions">
            <button type="button" class="plan-confirm" :disabled="confirming || creditsShort" @click="confirmPlan">
              {{ confirming ? 'Confirming…' : 'Confirm & execute' }}
            </button>
            <button type="button" class="soft-button" @click="requestChanges">Request changes</button>
            <span>Nothing is created until you confirm, and confirming approves the estimated credits. Usage is charged as the work runs. You can also just reply “confirm”.</span>
          </footer>
        </section>

        <section v-if="teamActivity.length" class="team-activity" aria-label="Team activity">
          <header><strong>Your team</strong><span>{{ teamWorking ? `${teamWorking} task${teamWorking === 1 ? '' : 's'} in progress` : 'All caught up' }}</span></header>
          <article v-for="item in teamActivity" :key="item.id" class="team-activity__item">
            <span class="team-activity__state" :class="`team-activity__state--${item.state}`">
              <span v-if="item.state === 'working'" class="typing-dots" aria-hidden="true"><i></i><i></i><i></i></span>{{ TEAM_STATE_LABELS[item.state] }}
            </span>
            <div class="team-activity__main">
              <strong>{{ item.title }}</strong>
              <small>{{ item.agent }} · {{ ago(item.updatedAt) }}
                <button v-if="item.state === 'failed'" type="button" class="team-activity__retry" :disabled="Boolean(retrying)" @click="retryTask(item.id)">
                  <RotateCcw :size="12" /> {{ retrying === item.id ? 'Retrying…' : 'Retry' }}</button></small>
              <div v-if="item.files.length" class="team-activity__files">
                <a v-for="file in item.files" :key="file.id" :href="file.url" target="_blank" rel="noopener noreferrer"><FileText :size="13" /> {{ file.filename }}</a>
              </div>
            </div>
          </article>
        </section>
      </div>
    </div>

    <div class="composer-wrap">
      <form class="composer" @submit.prevent="sendMessage">
        <textarea
          v-model="draft"
          rows="1"
          placeholder="Message your Manager…"
          @keydown.enter.exact.prevent="sendMessage"
        ></textarea>
        <div class="composer__bottom">
          <div>
            <button type="button" class="composer-icon" aria-label="Attach a file">
              <Paperclip :size="17" />
            </button>
            <label class="queue-toggle"
              ><input v-model="queued" type="checkbox" /><span></span> Queue without
              interrupting</label
            >
          </div>
          <button
            class="send-button"
            type="submit"
            :disabled="!draft.trim() || sending"
            aria-label="Send message"
          >
            <Send :size="17" />
          </button>
        </div>
      </form>
      <p>
        <UsersRound :size="12" /> Your Manager coordinates specialists. You never need to manage
        them directly.
      </p>
    </div>
  </div>
</template>
